import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, SectionCard, LoadingScreen, EmptyState, StatusBadge } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

const ROLE_COLORS = {
  student: { bg: 'rgba(16,185,129,0.2)', text: '#6ee7b7' },
  faculty: { bg: 'rgba(99,102,241,0.2)', text: '#a5b4fc' },
};

function VerifiedTable({ complaints, s, navigate }) {
  if (!complaints.length)
    return <EmptyState text="No complaints in this category" sub="Nothing here yet" />;
  return (
    <table style={s.table}>
      <thead style={s.thead}>
        <tr>
          <th style={s.th}>Complaint</th>
          <th style={s.th}>Filed By</th>
          <th style={s.th}>Department</th>
          <th style={s.th}>Verified On</th>
          <th style={s.th}>Resolution Due</th>
          <th style={s.th}>Status</th>
        </tr>
      </thead>
      <tbody>
        {complaints.map(c => (
          <tr key={c.id} className="ur-row-hover" onClick={() => navigate(`/complaints/${c.id}`)}>
            <td style={s.tdBold}>{c.title}</td>
            <td style={s.td}><span style={{ fontSize: 12 }}>{c.student_name || '—'}</span></td>
            <td style={s.td}><span style={{ fontSize: 12 }}>{c.department || '—'}</span></td>
            <td style={s.td}><span style={{ fontSize: 12 }}>{c.verified_at ? new Date(c.verified_at).toLocaleDateString() : '—'}</span></td>
            <td style={s.td}>
              {c.resolution_deadline
                ? <span style={{ fontSize: 12, color: new Date(c.resolution_deadline) < new Date() ? '#fca5a5' : 'rgba(255,255,255,0.8)', fontWeight: new Date(c.resolution_deadline) < new Date() ? 700 : 400 }}>
                    {new Date(c.resolution_deadline).toLocaleDateString()}
                  </span>
                : <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>—</span>}
            </td>
            <td style={s.td}><StatusBadge status={c.status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VerifiedComplaintsInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [tab, setTab] = useState('student');

  const fetchComplaints = useCallback(async () => {
    try {
      const res = await api.get('/admin/verified');
      setComplaints(Array.isArray(res.data) ? res.data : []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load complaints');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

  if (loading) return <LoadingScreen />;

  const studentComplaints = complaints.filter(c => c.submitter_role === 'student');
  const facultyComplaints = complaints.filter(c => c.submitter_role === 'faculty');

  const tabStyle = (active, role) => {
    const rc = ROLE_COLORS[role];
    return {
      padding: '6px 18px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
      border: `1px solid ${active ? rc.text : 'rgba(255,255,255,0.15)'}`,
      background: active ? rc.bg : 'rgba(255,255,255,0.05)',
      color: active ? rc.text : 'rgba(255,255,255,0.55)',
      transition: 'all 0.2s',
    };
  };

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar collapsed={false} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="Verified Complaints"
            subtitle="Verified and escalated to authority for resolution"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={fetchComplaints}>
                Refresh
              </button>
            }
          />
          <div style={s.content}>
            <PageHeader
              icon="✅"
              title="Verified Complaints"
              subtitle="Verified and escalated to authority for resolution"
              stats={[
                { label: 'Total Verified', value: complaints.length },
                { label: 'Students', value: studentComplaints.length },
                { label: 'Faculty', value: facultyComplaints.length },
              ]}
            />
            {error && <div style={s.alertErr}>{error}</div>}
            <SectionCard
              title="Verified Complaints"
              subtitle="These complaints have been sent to authority"
              delay="0.1s"
              action={
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={tabStyle(tab === 'student', 'student')} onClick={() => setTab('student')}>
                    Students ({studentComplaints.length})
                  </button>
                  <button style={tabStyle(tab === 'faculty', 'faculty')} onClick={() => setTab('faculty')}>
                    Faculty ({facultyComplaints.length})
                  </button>
                </div>
              }
            >
              <VerifiedTable
                complaints={tab === 'student' ? studentComplaints : facultyComplaints}
                s={s}
                navigate={navigate}
              />
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VerifiedComplaints() {
  return <ThemeProvider><VerifiedComplaintsInner /></ThemeProvider>;
}
